import { useMemo, useState } from 'react';
import { COLORS, badge, btn, input as inputStyle } from '../../utils/uiStyles';

// 경영지원팀 보드 담당자 태그 UI 공용 컴포넌트.
// 🔴 프로젝트 팝업에 인라인으로 있던 마크업을 그대로 옮긴 것이다 — DOM·aria-label·문구를 바꾸면
//    ProjectModal.test.jsx 와 보드 e2e(담당자 제외 / + 담당자 선택 / 담당자 추가)가 깨진다.

// 태그 라벨 = 이름(없으면 이메일) + 직급. 직급은 /assignees 가 찾았을 때만 붙여 준다.
export function personLabel(person) {
  if (!person) return '';
  return [person.name || person.email, person.position].filter(Boolean).join(' ');
}

// 🔴 카드·목록 표시는 직급 없이 이름만 쓴다(카드에 이름 정확일치 단언이 걸려 있다).
export function personName(person) {
  if (!person) return '';
  return person.name || person.email || '';
}

// 복수 담당자가 정본이지만, 조인테이블 백필 전 데이터는 레거시 단일 컬럼만 들고 온다.
export function assigneeIdsOf(item) {
  if (Array.isArray(item?.assignee_user_ids) && item.assignee_user_ids.length > 0) {
    return [...item.assignee_user_ids];
  }
  return item?.assignee_user_id ? [item.assignee_user_id] : [];
}

// personById 는 Map 이 기본이지만 평범한 객체도 받는다(호출부가 둘 다 쓰기 편하도록).
function lookupPerson(personById, id) {
  if (!personById) return null;
  return typeof personById.get === 'function' ? personById.get(id) : personById[id];
}

export function assigneeNames(ids, personById) {
  return (Array.isArray(ids) ? ids : []).map(
    (id) => personName(lookupPerson(personById, id)) || '담당자 정보 없음',
  );
}

// 카드처럼 폭이 좁은 자리용 — 앞에서 max 명만 이름으로 보이고 나머지는 '외 N명'으로 접는다.
export function summarizeAssigneeNames(ids, personById, max = 2) {
  const names = assigneeNames(ids, personById);
  return { names: names.slice(0, max), overflow: Math.max(0, names.length - max) };
}

export default function AssigneeTags({
  ids,
  people,
  onChange,
  groupLabel = '담당자',
  disabled = false,
  emptyText = '담당자 없음',
}) {
  const [picking, setPicking] = useState(false);
  const selected = useMemo(() => (Array.isArray(ids) ? ids : []), [ids]);
  const roster = useMemo(() => (Array.isArray(people) ? people : []), [people]);
  const personById = useMemo(
    () => new Map(roster.map((person) => [person.id, person])),
    [roster],
  );
  const candidates = roster.filter((person) => !selected.includes(person.id));

  const removeAssignee = (id) => onChange(selected.filter((value) => value !== id));

  const addAssignee = (event) => {
    const id = Number(event.target.value);
    if (id && !selected.includes(id)) onChange([...selected, id]);
    setPicking(false);
  };

  return (
    <div
      role="group"
      aria-label={groupLabel}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}
    >
      {selected.length === 0 && (
        <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{emptyText}</span>
      )}
      {selected.map((id) => {
        const label = personLabel(personById.get(id)) || `담당자 #${id}`;
        return (
          <span key={id} style={{ ...badge('default'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            {label}
            <button
              type="button"
              onClick={() => removeAssignee(id)}
              disabled={disabled}
              aria-label={`${label} 담당자 제외`}
              style={{ padding: 0, border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        );
      })}
      {picking && !disabled ? (
        <select
          aria-label="담당자 추가"
          value=""
          onChange={addAssignee}
          style={{ ...inputStyle, width: 'auto', minWidth: '150px', padding: '5px 8px', fontSize: '12px' }}
        >
          <option value="">담당자 선택</option>
          {candidates.map((person) => (
            <option key={person.id} value={person.id}>{personLabel(person)}</option>
          ))}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => setPicking(true)}
          disabled={disabled || candidates.length === 0}
          style={{ ...btn('secondary', 'sm'), opacity: (disabled || candidates.length === 0) ? 0.5 : 1 }}
        >
          + 담당자 선택
        </button>
      )}
    </div>
  );
}
